import Matrix from '@/organisms/cards/QuadrantActivities/Matrix';
import MatrixType from '@/organisms/cards/QuadrantActivities/MatrixType';
import mapActivityQuadrants from '@/organisms/cards/QuadrantActivities/mapActivityQuadrants';
import { DesignEntity, Position } from '@/lib/enums';

// The worst few carry the advice; the rest are noise at 40 words each.
const LIMIT = 4;

/**
 * The prioritisation matrix's bottom-left quadrant, as the suggestion request
 * carries it.
 *
 * These are the activities tagged neither strategically important nor
 * energising, aggregated by library activity and weighed against the hours of
 * everything the view holds — the same quadrant the chart beside it is drawn
 * from, read through the same map.
 *
 * @param {Object} scenario
 * @returns {Object|null} null where there is nothing to advise on
 */
export default function toPriorityQuadrant(scenario) {
  const entity = scenario?.entity;
  const title = entity?.name ?? entity?.title;

  if (!title) {
    return null;
  }

  const quadrantMap = mapActivityQuadrants({
    scenario,
    matrix: Matrix[MatrixType.PRIORITY],
  });

  const totalHours = quadrantMap.get(DesignEntity.ACTIVITY)?.__totalHours ?? 0;
  const quadrant = quadrantMap.get(Position.BOTTOM_LEFT);

  if (!totalHours || !quadrant?.activities?.size) {
    return null;
  }

  const activities = Array.from(quadrant.activities.values())
    .filter(
      (activity) =>
        Boolean(activity.__description) && activity.__aggregateHours > 0
    )
    .sort((a, b) => b.__aggregateHours - a.__aggregateHours)
    .slice(0, LIMIT)
    .map((activity) => ({
      description: activity.__description,
      hours: activity.__aggregateHours,
      hoursPercentage: (activity.__aggregateHours / totalHours) * 100,
    }));

  if (!activities.length) {
    return null;
  }

  return {
    // a group is advised to discuss with its team, a role with its manager
    scope:
      entity.__type === DesignEntity.GROUP
        ? DesignEntity.GROUP
        : DesignEntity.ROLE,
    title,
    activities,
  };
}
